import Image from "next/image";

import clsx from "clsx";

import type { FlightWidgets, FlightWidgetTab } from "@repo/ui/types";

interface WidgetNavBarProps {
  widgets: FlightWidgets;
  activeTab: FlightWidgetTab;
  onTabChanged: (tab: FlightWidgetTab) => void;
}

export default function WidgetNavBar({ widgets, activeTab, onTabChanged }: WidgetNavBarProps) {
  const { tabs } = widgets;

  const desktopTabs = tabs.sort((a, b) => a.sortOrder - b.sortOrder);

  // Get only Search Smart & Manage Your Flight
  const mobileTabs = tabs
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .filter((tab) => tab.sortOrder === 1 || tab.sortOrder === 2);

  return (
    <div className="relative">
      {/* Destop Nav  */}
      <nav
        aria-label="Widget navigation"
        className="hidden justify-center space-x-1 sm:flex sm:justify-start sm:space-x-3"
        data-testid="desktop-widget-navigation"
      >
        {desktopTabs.map((tab) => {
          const isActive = activeTab.id === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => onTabChanged(tab)}
              aria-current={isActive ? "page" : undefined}
              className={clsx(
                "flex cursor-pointer flex-row items-center! border-b-[3px] border-transparent px-2 py-1 text-xs transition-all duration-500 ease-in-out sm:items-start sm:space-x-2 sm:text-sm md:text-base lg:px-5 lg:py-3",
                isActive
                  ? "border-b-brand-cyan text-brand-blue rounded-t-lg bg-white"
                  : "text-brand-gray mb-2 rounded-lg bg-gray-100 hover:bg-white",
              )}
              data-testid={`${String(tab.id).toLowerCase()}-nav-btn`}
            >
              <Image
                src={isActive ? tab.activeIconUrl : tab.inactiveIconUrl}
                alt={tab.name}
                width={20}
                height={20}
              />
              <span className="text-center">{tab.name}</span>
            </button>
          );
        })}
      </nav>

      {/* Mobile Nav */}
      <nav
        aria-label="Widget navigation"
        className="flex w-full justify-between space-x-3 sm:hidden"
        data-testid="mobile-widget-navigation"
      >
        {mobileTabs.map((tab) => {
          const isActive = activeTab.id === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChanged(tab)}
              aria-current={isActive ? "page" : undefined}
              className={clsx(
                "flex w-full flex-row items-center justify-center space-x-2 py-3 text-base transition-all duration-1000 ease-in-out",
                isActive
                  ? "text-brand-blue rounded-t-lg border-b-transparent bg-white"
                  : "text-brand-gray mb-2 rounded-lg bg-gray-100 hover:bg-white",
              )}
              data-testid={`${String(tab.id).toLowerCase()}-nav-btn`}
            >
              <Image
                src={
                  isActive
                    ? (tab.mobileActiveIconUrl ?? tab.activeIconUrl)
                    : (tab.mobileInactiveIconUrl ?? tab.inactiveIconUrl)
                }
                alt={tab.name}
                width={20}
                height={20}
              />
              <span className="text-center">{tab.mobileName}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
